import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, X, Scan } from 'lucide-react';

const BarcodeScanner = ({ isOpen, onClose, onScan }) => {
  const [manualInput, setManualInput] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isOpen && showCamera) {
      startCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [isOpen, showCamera]); // eslint-disable-line

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Unable to access camera. Please enter barcode manually.');
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const handleManualSubmit = () => {
    if (manualInput.trim()) {
      onScan(manualInput.trim());
      setManualInput('');
      setShowCamera(false);
      onClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    setManualInput('');
    setShowCamera(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="w-5 h-5 text-orange-500" />
            Find Asset by Barcode
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {!showCamera ? (
            <>
              <div>
                <Label>Enter Barcode/Asset Tag</Label>
                <Input
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="e.g., EXT-001, FIR-12345"
                  className="bg-gray-800 border-gray-700 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleManualSubmit()}
                  autoFocus
                />
              </div>

              <Button
                onClick={handleManualSubmit}
                className="w-full bg-orange-600 hover:bg-orange-700"
                disabled={!manualInput.trim()}
              >
                <Scan className="w-4 h-4 mr-2" />
                Find Asset
              </Button>

              <div className="text-center">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-700"></span>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-gray-900 px-2 text-gray-400">OR</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setShowCamera(true)}
                variant="outline"
                className="w-full"
              >
                <Camera className="w-4 h-4 mr-2" />
                Use Camera
              </Button>
            </>
          ) : (
            <>
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="border-4 border-orange-500 w-48 h-48 rounded-lg"></div>
                </div>
              </div>
              
              <div className="text-center text-sm text-gray-400">
                <p>Position the barcode within the orange frame</p>
                <p className="mt-1 text-yellow-400">Note: Manual scanning library coming soon</p>
              </div>

              <Button
                onClick={() => setShowCamera(false)}
                variant="outline"
                className="w-full"
              >
                Enter Manually Instead
              </Button>
            </>
          )}

          <Button
            onClick={handleClose}
            variant="ghost"
            className="w-full"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
